import { useEffect, useEffectEvent } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { DEVICES_URI, sensorsQueries } from 'src/api/queryKeys';
import { signalR } from 'src/signalR';
import type {
  FanguardApiContractsBoxBatteryStateModelEnum,
  FanguardApiContractsDevicesDeviceModel,
  FanguardApiContractsSensorSensorBeatModel,
  FanguardApiContractsSensorSensorModel,
} from 'src/api/generated';
import type { PagedResponse } from 'src/types/GlobalTypes';

export const DEVICE_STATUS_CHANGED_EVENT = 'DeviceStatusChanged';

export interface DeviceStatusChangedPayload {
  id?: string | null;
  batteryLevel?: number | null;
  chargingStatus?: string | null;
  status?: 'online' | 'offline' | null;
  sensorPlacementName?: string | null;
  detailedStatus?: string | null;
  name?: string | null;
  snapshotUrl?: string | null;
}

type DevicePatch = Partial<FanguardApiContractsDevicesDeviceModel>;
type LatestBeatPatch = Partial<FanguardApiContractsSensorSensorBeatModel>;

const clampBatteryLevel = (level: number) => Math.min(100, Math.max(0, level));

export const mapPayloadToDevicePatch = (payload: DeviceStatusChangedPayload): DevicePatch => {
  const patch: DevicePatch = {};

  if (typeof payload.batteryLevel === 'number') {
    patch.batteryLevel = clampBatteryLevel(payload.batteryLevel);
  }
  if (payload.chargingStatus) {
    patch.batteryState = payload.chargingStatus as unknown as FanguardApiContractsBoxBatteryStateModelEnum;
  }
  if (payload.status === 'online' || payload.status === 'offline') {
    patch.isOnline = payload.status === 'online';
  }
  if (payload.sensorPlacementName != null) {
    patch.placementName = payload.sensorPlacementName;
  }
  if (payload.name) {
    patch.name = payload.name;
  }
  if (payload.snapshotUrl) {
    patch.snapshotUrl = payload.snapshotUrl;
  }

  return patch;
};

export const mapPayloadToLatestBeatPatch = (payload: DeviceStatusChangedPayload): LatestBeatPatch => {
  const patch: LatestBeatPatch = {};

  if (typeof payload.batteryLevel === 'number') {
    patch.batteryLevel = clampBatteryLevel(payload.batteryLevel);
  }
  if (payload.chargingStatus) {
    patch.batteryState = payload.chargingStatus as unknown as FanguardApiContractsBoxBatteryStateModelEnum;
  }

  return patch;
};

const isPagedDevices = (data: unknown): data is PagedResponse<FanguardApiContractsDevicesDeviceModel> => {
  return !!data && typeof data === 'object' && Array.isArray((data as { items?: unknown }).items);
};

export const useDeviceStatusLiveUpdate = () => {
  const queryClient = useQueryClient();

  const onDeviceStatusChanged = useEffectEvent((payload: DeviceStatusChangedPayload) => {
    if (!payload?.id) return;

    const devicePatch = mapPayloadToDevicePatch(payload);
    const latestBeatPatch = mapPayloadToLatestBeatPatch(payload);

    if (Object.keys(devicePatch).length === 0 && Object.keys(latestBeatPatch).length === 0) {
      return;
    }

    if (Object.keys(devicePatch).length > 0) {
      queryClient.setQueriesData<PagedResponse<FanguardApiContractsDevicesDeviceModel>>(
        {
          predicate: (query) => {
            const key = query.queryKey;
            return Array.isArray(key) && key[0] === DEVICES_URI && key[1] === 'list';
          },
        },
        (old) => {
          if (!isPagedDevices(old)) return old;
          let didChange = false;
          const items = old.items.map((device) => {
            if (device.id !== payload.id) return device;
            didChange = true;
            return { ...device, ...devicePatch };
          });
          return didChange ? { ...old, items } : old;
        },
      );
    }

    queryClient.setQueryData<FanguardApiContractsSensorSensorModel | undefined>(
      sensorsQueries.detail(payload.id).queryKey,
      (old) => {
        if (!old) return old;
        const next: FanguardApiContractsSensorSensorModel = { ...old };
        if (devicePatch.isOnline !== undefined) {
          next.isOnline = devicePatch.isOnline;
        }
        if (Object.keys(latestBeatPatch).length > 0) {
          next.latestBeat = next.latestBeat ? { ...next.latestBeat, ...latestBeatPatch } : latestBeatPatch;
        }
        return next;
      },
    );
  });

  useEffect(() => {
    const handler = onDeviceStatusChanged as (...args: unknown[]) => void;
    signalR.addListener(DEVICE_STATUS_CHANGED_EVENT, handler);
    return () => {
      signalR.removeListener(DEVICE_STATUS_CHANGED_EVENT, handler);
    };
  }, []);
};

export default useDeviceStatusLiveUpdate;
